import React, { useMemo, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { BOWL_OPTIONS, INGREDIENT_IMAGES } from '../data/menuData';
import { ProductOption, CartItemType } from '../types/types';
import { CONFIG } from '../data/config';
interface BowlBuilderProps {
  onClose: () => void;
  onSave: (item: CartItemType) => void;
  initialItem?: CartItemType;
}
const STEPS = [{
  id: 'base',
  title: 'Elegí la base',
  subtitle: 'Obligatorio — elegí una base para tu bowl',
  required: true,
  multi: false,
  options: BOWL_OPTIONS.bases
}, {
  id: 'proteina',
  title: 'Sumá tu proteína',
  subtitle: 'Opcional — podés avanzar sin proteína si preferís',
  required: false,
  multi: false,
  options: BOWL_OPTIONS.proteinas
}, {
  id: 'toppings',
  title: 'Toppings frescos',
  subtitle: 'Opcional — elegí los que quieras o avanzá sin ninguno',
  required: false,
  multi: true,
  options: BOWL_OPTIONS.toppings
}, {
  id: 'toppingsPremium',
  title: 'Toppings premium',
  subtitle: 'Opcional — tienen costo adicional por unidad',
  required: false,
  multi: true,
  options: BOWL_OPTIONS.toppingsPremium
}, {
  id: 'aderezos',
  title: 'Aderezo',
  subtitle: 'Opcional — elegí los que más te gusten',
  required: false,
  multi: true,
  options: BOWL_OPTIONS.aderezos,
  note: 'Consultar opciones disponibles'
}, {
  id: 'semillas',
  title: 'Semillas',
  subtitle: 'Opcional — le dan un toque crunchy a tu bowl',
  required: false,
  multi: true,
  options: BOWL_OPTIONS.semillas
}, {
  id: 'comentarios',
  title: 'Comentarios',
  subtitle: 'Opcional — si no tenés comentarios, podés agregar tu bowl directamente',
  required: false,
  multi: false,
  options: []
}];
export function BowlBuilder({
  onClose,
  onSave,
  initialItem
}: BowlBuilderProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selections, setSelections] = useState<any>(initialItem?.selections || {
    base: undefined,
    proteina: undefined,
    toppings: [],
    toppingsPremium: [],
    aderezos: [],
    semillas: [],
    comentarios: ''
  });
  const step = STEPS[currentStep];
  const handleSelect = (option: ProductOption) => {
    if (step.multi) {
      const currentList = selections[step.id] || [];
      const exists = currentList.find((item: ProductOption) => item.id === option.id);
      if (exists) {
        setSelections({
          ...selections,
          [step.id]: currentList.filter((item: ProductOption) => item.id !== option.id)
        });
      } else {
        setSelections({
          ...selections,
          [step.id]: [...currentList, option]
        });
      }
    } else {
      if (selections[step.id]?.id === option.id && !step.required) {
        setSelections({
          ...selections,
          [step.id]: undefined
        });
      } else {
        setSelections({
          ...selections,
          [step.id]: option
        });
      }
    }
  };
  const isStepValid = () => {
    if (step.required && !selections[step.id]) return false;
    return true;
  };
  const nextStep = () => {
    if (isStepValid() && currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const calculateSubtotal = () => {
    let total = 0;
    if (selections.base) total += selections.base.price;
    if (selections.proteina) total += selections.proteina.price;
    total += (selections.toppings || []).reduce((sum: number, t: ProductOption) => sum + t.price, 0);
    total += (selections.toppingsPremium || []).reduce((sum: number, t: ProductOption) => sum + t.price, 0);
    total += (selections.aderezos || []).reduce((sum: number, t: ProductOption) => sum + t.price, 0);
    total += (selections.semillas || []).reduce((sum: number, t: ProductOption) => sum + t.price, 0);
    return total;
  };
  const handleSave = () => {
    if (!selections.base) return; // Safety check
    const item: CartItemType = {
      id: initialItem?.id || `bowl_${Date.now()}`,
      productId: 'custom_bowl',
      name: 'Bowl Personalizado',
      basePrice: 0,
      quantity: initialItem?.quantity || 1,
      selections,
      subtotal: calculateSubtotal(),
      type: 'bowl'
    };
    onSave(item);
  };
  // Get selection count for current step
  const getSelectionCount = () => {
    const val = selections[step.id];
    if (Array.isArray(val)) return val.length;
    if (val) return 1;
    return 0;
  };
  // Get all selected ingredients for the visual progress strip
  const getAllSelectedIngredients = () => {
    const all: ProductOption[] = [];
    if (selections.base) all.push(selections.base);
    if (selections.proteina) all.push(selections.proteina);
    if (selections.toppings) all.push(...selections.toppings);
    if (selections.toppingsPremium) all.push(...selections.toppingsPremium);
    if (selections.aderezos) all.push(...selections.aderezos);
    if (selections.semillas) all.push(...selections.semillas);
    return all;
  };
  const selectedIngredients = getAllSelectedIngredients();
  return <motion.div initial={{
    opacity: 0,
    y: 50
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 50
  }} className="fixed inset-0 z-50 bg-carbon flex flex-col max-w-md mx-auto shadow-2xl" data-id="element-15">
      {/* Header */}
      <div className="bg-surface px-4 py-4 flex items-center justify-between shadow-sm z-20 border-b border-gray-800" data-id="element-16">
        <button onClick={onClose} className="p-2 -ml-2 text-gray-400 hover:text-white" data-id="element-17">
          <XIcon className="w-6 h-6" data-id="element-18" />
        </button>
        <h2 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-19">
          Armá tu Bowl
        </h2>
        <div className="w-10" data-id="element-20"></div>
      </div>

      {/* Progress Bar */}
      <div className="bg-surface-light h-1.5 w-full" data-id="element-21">
        <motion.div className="h-full bg-verde" initial={{
        width: 0
      }} animate={{
        width: `${(currentStep + 1) / STEPS.length * 100}%`
      }} transition={{
        ease: 'easeInOut'
      }} data-id="element-22" />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-24" data-id="element-23">
        <AnimatePresence mode="wait" data-id="element-24">
          <motion.div key={currentStep} initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} transition={{
          duration: 0.2
        }} data-id="element-25">
            <div className="mb-6" data-id="element-26">
              <div className="flex items-center gap-2 mb-1" data-id="element-27">
                <span className="text-2xl" data-id="element-28">{step.emoji}</span>
                <h3 className="text-2xl font-heading font-bold text-white tracking-wide" data-id="element-29">
                  {step.title}
                </h3>
              </div>
              <div className="flex items-center justify-between" data-id="element-30">
                <p className="text-gray-400 text-sm" data-id="element-31">{step.subtitle}</p>
                {step.required ? <span className="text-xs font-medium text-terracota bg-terracota/10 px-2 py-0.5 rounded-full border border-terracota/20" data-id="element-32">
                    Requerido
                  </span> : <span className="text-xs font-medium text-gray-400 bg-surface-light px-2 py-0.5 rounded-full border border-gray-700" data-id="element-33">
                    Opcional
                  </span>}
              </div>
            </div>

            {/* Selection Counter for Multi-select */}
            {step.multi && <div className="mb-4 flex items-center justify-between bg-surface-light p-3 rounded-xl border border-gray-800" data-id="element-34">
                <span className="text-sm text-gray-300" data-id="element-35">Seleccionados:</span>
                <span className={`text-sm font-bold ${getSelectionCount() === step.max ? 'text-terracota' : 'text-verde'}`} data-id="element-36">
                  {getSelectionCount()} / {step.max}
                </span>
              </div>}

            {step.note && <p className="text-terracota text-sm mb-4 font-medium" data-id="element-37">
                {step.note}
              </p>}

            {step.id === 'comentarios' ? <div data-id="element-38">
                <textarea value={selections.comentarios} onChange={e => setSelections({
              ...selections,
              comentarios: e.target.value
            })} placeholder="Ejemplo: sin cebolla, alérgico a lácteos, cambiar tomate por pepino, etc." className="w-full h-32 p-4 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none resize-none text-white placeholder-gray-500" data-id="element-39" />
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1" data-id="element-40">
                  <InfoIcon className="w-3 h-3" data-id="element-41" />
                  Si no tenés comentarios, simplemente tocá "Agregar" abajo
                </p>

                {/* Visual Preview — Stacked Bowl Composition */}
                {selectedIngredients.length > 0 && <div className="mt-6" data-id="element-42">
                    <h4 className="font-heading font-semibold text-base mb-3 text-white tracking-wide" data-id="element-43">
                      Tu Bowl
                    </h4>
                    <div className="rounded-2xl overflow-hidden border border-gray-800 shadow-sm bg-surface-light relative" data-id="element-44">
                      <div className="relative w-full h-52 flex items-center justify-center" data-id="element-45">
                        {/* Background bowl image */}
                        <div className="absolute inset-0 opacity-10" data-id="element-46">
                          <img src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=600&h=400&fit=crop" alt="" className="w-full h-full object-cover blur-sm" data-id="element-47" />
                        </div>
                        {/* Ingredient stack */}
                        <div className="relative w-48 h-48" data-id="element-48">
                          {selectedIngredients.map((ing, idx) => {
                      const total = selectedIngredients.length;
                      const angle = idx / total * 360;
                      const radius = total <= 3 ? 38 : 52;
                      const x = Math.cos(angle * Math.PI / 180) * radius;
                      const y = Math.sin(angle * Math.PI / 180) * radius;
                      const size = total <= 2 ? 84 : total <= 4 ? 72 : 60;
                      const rotation = idx * 12 - 15;
                      return <div key={ing.id} className="absolute rounded-full overflow-hidden shadow-xl border-2 border-gray-700" style={{
                        width: size,
                        height: size,
                        left: `calc(50% + ${x}px - ${size / 2}px)`,
                        top: `calc(50% + ${y}px - ${size / 2}px)`,
                        zIndex: idx + 1,
                        transform: `rotate(${rotation}deg)`
                      }} data-id="element-49">
                                {INGREDIENT_IMAGES[ing.id] ? <img src={INGREDIENT_IMAGES[ing.id]} alt={ing.name} className="w-full h-full object-cover" data-id="element-50" /> : <div className="w-full h-full bg-surface flex items-center justify-center text-white font-bold text-xs" data-id="element-51">
                                    {ing.name.charAt(0)}
                                  </div>}
                              </div>;
                    })}
                        </div>
                      </div>
                      <div className="bg-surface px-4 py-3 border-t border-gray-800" data-id="element-52">
                        <div className="flex flex-wrap gap-1.5 justify-center" data-id="element-53">
                          {selectedIngredients.map(ing => <span key={ing.id} className="text-[11px] bg-surface-light text-gray-300 px-2.5 py-1 rounded-full font-medium border border-gray-700" data-id="element-54">
                              {ing.name}
                            </span>)}
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-2" data-id="element-55">
                          {selectedIngredients.length} ingrediente
                          {selectedIngredients.length !== 1 ? 's' : ''}{' '}
                          seleccionado
                          {selectedIngredients.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>}
              </div> : <div className="space-y-3" data-id="element-56">
                {step.options.map(option => {
              const isSelected = step.multi ? selections[step.id]?.some((item: ProductOption) => item.id === option.id) : selections[step.id]?.id === option.id;
              return <button key={option.id} onClick={() => handleSelect(option)} className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all ${isSelected ? 'border-verde bg-verde/10' : 'border-gray-700 bg-surface hover:border-gray-500'}`} data-id="element-57">
                      <div className="flex items-center gap-3" data-id="element-58">
                        {INGREDIENT_IMAGES[option.id] && <img src={INGREDIENT_IMAGES[option.id]} alt="" className={`w-10 h-10 rounded-full object-cover border ${isSelected ? 'border-verde' : 'border-gray-700'}`} loading="lazy" data-id="element-59" />}
                        <span className={`font-medium ${isSelected ? 'text-verde' : 'text-gray-200'}`} data-id="element-60">
                          {option.name}
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-61">
                        {option.price > 0 && <span className={`text-sm ${isSelected ? 'text-verde/80' : 'text-gray-400'}`} data-id="element-62">
                            +{CONFIG.CURRENCY_SYMBOL}
                            {option.price}
                          </span>}
                        {option.price === 0 && <span className="text-xs text-gray-500" data-id="element-63">
                            Incluido
                          </span>}
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center border ${isSelected ? 'bg-verde border-verde text-carbon' : 'border-gray-600'}`} data-id="element-64">
                          {isSelected && <CheckIcon className="w-4 h-4" data-id="element-65" />}
                        </div>
                      </div>
                    </button>;
            })}
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="bg-surface border-t border-gray-800 p-4 flex items-center justify-between gap-3 sticky bottom-0 z-20" style={{
      paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))'
    }} data-id="element-66">
        {currentStep > 0 ? <button onClick={prevStep} className="p-3 rounded-xl bg-surface-light text-gray-400 hover:text-white transition-colors min-w-[48px] min-h-[48px] flex items-center justify-center border border-gray-700" data-id="element-67">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-68" />
          </button> : <div className="w-[48px]" data-id="element-69"></div>}

        <div className="flex-1 flex flex-col items-center" data-id="element-70">
          <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold" data-id="element-71">
            Subtotal
          </span>
          <span className="font-bold text-lg text-white" data-id="element-72">
            {CONFIG.CURRENCY_SYMBOL}
            {calculateSubtotal().toLocaleString()}
          </span>
        </div>

        {currentStep < STEPS.length - 1 ? <button onClick={nextStep} disabled={!isStepValid()} className={`px-5 py-3 rounded-xl font-semibold flex items-center gap-2 transition-colors min-h-[48px] border border-white/10 ${isStepValid() ? 'bg-verde text-carbon shadow-lg shadow-verde/20' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`} data-id="element-73">
            {!step.required && getSelectionCount() === 0 ? 'Saltar' : 'Siguiente'}{' '}
            <ChevronRightIcon className="w-5 h-5" data-id="element-74" />
          </button> : <button onClick={handleSave} className="px-5 py-3 rounded-xl font-semibold flex items-center gap-2 bg-terracota text-white shadow-lg shadow-terracota/20 transition-colors min-h-[48px] border border-white/10" data-id="element-75">
            Agregar <CheckIcon className="w-5 h-5" data-id="element-76" />
          </button>}
      </div>
    </motion.div>;
}